(() => {
  'use strict';
  if (customElements.get('header-action-hover')) return;
  customElements.define('header-action-hover', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.header = this.closest('smart-header');
      this.indicator = this.querySelector('.header-actions__highlight');
      if (!this.header || !this.indicator) return;
      this.controls = [...this.header.querySelectorAll('.header-actions > a,.header-actions > button,.header-nav > details > summary,.header-nav > nav > ul > li > .header-nav-link,.header-nav > nav > ul > li > details > summary')];
      this.events = new AbortController();
      const signal = this.events.signal;
      this.media = matchMedia('(min-width:990px) and (hover:hover) and (pointer:fine)');
      this.bounds = new Map();
      this.header.classList.add('has-header-hover');
      this.controls.forEach(control => control.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse' || !this.media.matches) return;
        if (document.documentElement.dataset.headerPanel === 'search' || document.documentElement.dataset.headerPanelPending === 'search') return;
        this.pointerTarget = control;
        clearTimeout(this.clearTimer);
        this.paint(control);
      }, {signal}));
      this.header.addEventListener('pointerleave', () => {
        this.pointerTarget = null;
        this.settle();
      }, {signal});
      this.header.addEventListener('pointerover', event => {
        if (event.pointerType !== 'mouse') return;
        if (event.target.closest('.mega-panel')) {
          this.pointerTarget = null;
          const control = this.panelControl();
          if (control) this.paint(control);
        } else if (event.target.closest('.header-utility,.header-search-desktop,.header-voice,.header-logo,.header-advice-pill,.header-featured')) {
          this.pointerTarget = null;
          this.settle();
        }
      }, {signal});
      this.header.addEventListener('focusin', event => {
        const control = event.target.closest('a,button,summary');
        if (this.controls.includes(control) && control.matches(':focus-visible')) {
          this.pointerTarget = null;
          clearTimeout(this.clearTimer);
          this.paint(control);
        }
      }, {signal});
      this.header.addEventListener('focusout', () => { if (!this.pointerTarget) this.settle(); }, {signal});
      const refresh = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.measure(); });
      };
      this.observer = new ResizeObserver(refresh);
      [this.header,...this.controls].forEach(node => this.observer.observe(node));
      this.stateObserver = new MutationObserver(() => {
        const control = this.pointerTarget || this.panelControl();
        if (control) this.paint(control);
        else this.settle();
      });
      this.stateObserver.observe(this.header,{subtree:true,attributes:true,attributeFilter:['open','aria-expanded']});
      window.addEventListener('resize',refresh,{passive:true,signal});
      window.addEventListener('blur',() => { this.pointerTarget = null; this.settle(); },{signal});
      this.media.addEventListener('change',() => { this.pointerTarget = null; this.clear(); refresh(); },{signal});
      document.addEventListener('olea:header-panel-open',refresh,{signal});
      document.addEventListener('olea:header-panel-close',() => this.settle(),{signal});
      this.measure();
    }
    panelControl() {
      const type = document.documentElement.dataset.headerPanel;
      if (type === 'assistant') return this.controls.find(control => control.matches('[data-assistant-open]'));
      if (type === 'category') return this.controls.find(control => control.matches('summary') && control.getAttribute('aria-expanded') === 'true');
      return null;
    }
    measure() {
      if (!this.media.matches) {
        this.bounds.clear();
        this.clear();
        return;
      }
      const origin = this.header.getBoundingClientRect();
      this.headerHeight = origin.height;
      this.bounds.clear();
      const bridges = [];
      this.controls.forEach(control => {
        const rect = control.getBoundingClientRect();
        if (rect.width && rect.height) {
          const local = {x:rect.left-origin.left,y:rect.top-origin.top,width:rect.width,height:rect.height};
          this.bounds.set(control,local);
          if (control.classList.contains('header-all')) {
            bridges.push([control, Math.max(0,this.headerHeight-local.y-local.height)+1]);
          }
        }
      });
      bridges.forEach(([control, height]) => control.style.setProperty('--header-all-bridge-height', `${height}px`));
      const control = this.pointerTarget || this.panelControl() || this.active;
      if (control) this.paint(control);
    }
    paint(control) {
      const rect = this.bounds.get(control);
      if (!this.media.matches || !rect) { this.clear(); return; }
      clearTimeout(this.clearTimer);
      const summary = control.matches('summary');
      const all = control.classList.contains('header-all');
      const connected = summary && control.getAttribute('aria-expanded') === 'true';
      const extra = all ? 0 : summary ? 12 : control.classList.contains('header-nav-link') ? 10 : 8;
      const height = connected ? this.headerHeight - rect.y : 44;
      const bounds = {x:rect.x-extra,y:connected ? rect.y : rect.y+(rect.height-height)/2,width:rect.width+extra*2,height};
      for (const [name,value] of Object.entries(bounds)) this.style.setProperty(`--action-${name}`,`${value}px`);
      this.style.setProperty('--action-radius',connected ? `${all ? 22 : 12}px ${all ? 22 : 12}px 0 0` : '999px');
      this.controls.forEach(item => item.classList.toggle('is-action-highlighted',item===control));
      this.active = control;
      this.classList.add('has-action-highlight');
    }
    settle() {
      clearTimeout(this.clearTimer);
      const focused = this.controls.find(control => control === document.activeElement && control.matches(':focus-visible'));
      const control = this.pointerTarget || focused || this.panelControl();
      if (control) this.paint(control);
      else this.clearTimer = setTimeout(() => this.clear(),110);
    }
    clear() {
      clearTimeout(this.clearTimer);
      this.active = null;
      this.classList.remove('has-action-highlight');
      this.controls?.forEach(control => control.classList.remove('is-action-highlighted'));
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.stateObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.clear();
      this.header?.classList.remove('has-header-hover');
      this.events = null;
      this.frame = null;
      this.pointerTarget = null;
    }
  });
})();

